/* Tool rail + active-tool switching. */
import { DOOR_TYPES } from './types.ts';
import { $, state } from './state.ts';
import { FIXTURES } from './fixtures.ts';
import { canvas, hideLenBox, requestDraw } from './render.ts';
import { renderPanel } from './panel.ts';

export const TOOLS = [
  { id: 'select', key: 'V', name: 'Select', desc: 'Move, resize and edit things', icon: '<path d="M4 2l10 6-4.5 1.2L12 14l-2.2 1-2.4-4.6L4 13z"/>' },
  { id: 'room', key: 'A', name: 'Room', desc: 'Drag a rectangle to draw a room', icon: '<rect x="2" y="3" width="12" height="10" rx="1"/><path d="M2 13v-3.5M14 13v-3.5"/>' },
  { id: 'wall', key: 'W', name: 'Wall', desc: 'Draw walls corner to corner', icon: '<path d="M2 13h12M2 9h12M2 5h12M6 5v4M10 9v4M6 13v.01M10 5v.01"/>' },
  { id: 'constrain', key: 'C', name: 'Constrain', desc: 'Pin relationships between walls: square, parallel, distance…', icon: '<path d="M3 13h10M3 13V3M3 8h4v5"/>' },
  { id: 'door', key: 'D', name: 'Door', desc: 'Click a wall to add a door', icon: '<path d="M2 14h12M4 14V4h1M13 14A9 9 0 0 0 5 4"/>' },
  { id: 'window', key: 'N', name: 'Window', desc: 'Click a wall to add a window', icon: '<path d="M1 6h14M1 8h14M1 10h14M1 5v6M15 5v6"/>' },
  { id: 'fixture', key: 'X', name: 'Furniture', desc: 'Furniture, fixtures and appliances', icon: '<path d="M2 9V6a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v3M2 9h12v4H2zM4 13v1.5M12 13v1.5"/>' },
  { id: 'label', key: 'T', name: 'Text', desc: 'Place a text label', icon: '<path d="M3 4V2.5h10V4M8 2.5v11M6 13.5h4"/>' },
  { id: 'measure', key: 'M', name: 'Measure', desc: 'Measure between two points', icon: '<path d="M2 11 11 2l3 3-9 9zM5.5 8.5l1.2 1.2M8 6l1.2 1.2M10.5 3.5l1.2 1.2"/>' },
];

/** Rail sections, top to bottom: select · build · openings · furnish/annotate ·
    measure. Just the necessities — one Door button covers the whole door
    family (pick the variant in the right panel; U/I/B/P/O still jump straight
    to a variant). A thin rule separates groups. */
export const TOOL_GROUPS: string[][] = [
  ['select'],
  ['room', 'wall', 'constrain'],
  ['door', 'window'],
  ['fixture', 'label'],
  ['measure'],
];

export const HINTS: Record<string, string> = {
  select: 'Drag to move · handles resize/rotate · doors: drag to the side they should open to, click the other jamb to move the hinge · ⇧click multi-select · click a dimension to edit it · ⌘D duplicate',
  room: 'Drag to draw a room · ⇧ keeps it square · corners snap to existing walls · or type a size in the panel',
  wall: 'Click to start, click to continue, double-click / Esc to end · type a length (e.g. 10\'6) then Enter · hover a corner/wall to join · ⌥ free angle',
  constrain: 'Pick a relationship in the panel, then click two walls — the first one stays put · H / V / Fix take one click · Esc when done',
  door: 'Click a wall to place a door · H flips hinge · S flips swing',
  double: 'Click a wall to place a double (French) door · S flips swing',
  bifold: 'Click a wall to place a bifold closet door · S flips which side it folds toward',
  slider: 'Click a wall to place a bypass sliding door',
  pocket: 'Click a wall to place a pocket door · H flips which side it slides into',
  cased: 'Click a wall to place an open (cased) passage',
  window: 'Click a wall to place a window',
  fixture: 'Drag a tile from the catalog onto the plan (or click it, then click where it goes) · type to search · snaps flush to walls (⌥ free) · R rotates',
  label: 'Click to place text',
  measure: 'Click-drag to measure · Esc clears',
};

export function buildRail(): void {
  const rail = $('#rail');
  rail.innerHTML = '';
  let first = true;
  for (const group of TOOL_GROUPS) {
    if (!first) { const s = document.createElement('div'); s.className = 'rail-sep'; rail.appendChild(s); }
    first = false;
    for (const id of group) {
      const t = TOOLS.find(x => x.id === id)!;
      const active = id === 'door' ? DOOR_TYPES.includes(state.tool as any) : state.tool === id;
      const b = document.createElement('button');
      b.className = 'tool' + (active ? ' active' : '');
      b.title = id === 'door' ? `${t.desc} — pick the type in the panel (${t.key})` : `${t.desc} (${t.key})`;
      b.dataset.tool = id;
      b.setAttribute('aria-label', `${t.name} tool, ${t.key}`);
      b.setAttribute('aria-pressed', String(active));
      b.innerHTML = `<svg viewBox="0 0 16 16">${t.icon}</svg><span class="lbl">${t.name}</span><span class="kbd">${t.key}</span>`;
      b.addEventListener('click', () => setTool(id === 'door' ? state.doorType : id));
      rail.appendChild(b);
    }
  }
}

export function setTool(id: string): void {
  state.tool = id;
  if (DOOR_TYPES.includes(id as any)) state.doorType = id;
  state.wallDraft = null; state.roomDraft = null; state.measure = id === 'measure' ? state.measure : null;
  state.constrainFirst = null;
  if (id === 'fixture') {
    const def = FIXTURES[state.fixtureType];
    state.placeGhost = { w: def.w, h: def.h, rot: 0, x: state.lastMouse.x, y: state.lastMouse.y };
  } else state.placeGhost = null;
  hideLenBox();
  // small screens: a tool with its own panel (catalog, door types, room size) opens the sheet; Select puts it away
  $('#main').classList.toggle('sheetOpen', id === 'fixture' || id === 'room' || id === 'constrain' || DOOR_TYPES.includes(id as any));
  canvas.style.cursor = (id === 'select') ? 'default' : 'crosshair';
  $('#hint').textContent = HINTS[id] || '';
  buildRail(); renderPanel(); requestDraw();
}
